import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, EMPTY, map, Observable, of, share, startWith, Subject, switchMap} from 'rxjs';
import {getApiUrl} from '../../core/helpers/env.helper';
import {exponentialBackoffRetry} from '../../core/operators/retry.operator';
import {ApiResponse} from '../../core/model/api-response';
import {TeamMember, TeamMembersResponse} from './team-member.model';
import {TeamMember as AddTeamMemberModel} from './add-member/team-member.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

export const addTeamMemberStatuses = [
  'idle',
  'adding',
  'done',
  'error',
] as const;

export type AddTeamMemberStatus = typeof addTeamMemberStatuses[number];

@Injectable({
  providedIn: 'root'
})
export class TeamMembersService {
  private readonly http = inject(HttpClient);
  private readonly addTeamMembersSubject = new Subject<AddTeamMemberModel[]>();
  private readonly manualStatusSubject = new Subject<AddTeamMemberStatus>();

  // Define addTeamMemberStatus$ as an observable that combines:
  // 1. Initial 'idle' status
  // 2. Manual status updates (like 'adding')
  // 3. API request statuses triggered by addMembers
  addTeamMemberStatus$: Observable<AddTeamMemberStatus> = of('idle' as AddTeamMemberStatus).pipe(
    // Merge with manual status updates
    switchMap(initialStatus => {
      // Create an observable for API requests that will be triggered by addMembers
      const apiRequestStatus$ = this.addTeamMembersSubject.pipe(
        // For each team members array emitted, make an API request
        switchMap(teamMembers => {
          // Return the API request observable
          return getApiUrl('iam').pipe(
            switchMap(url =>
              this.http.post<{ success: boolean }>(`${url}/orgs/team-members`, teamMembers).pipe(
                exponentialBackoffRetry(),
                // On success, emit 'done'
                map(() => 'done' as AddTeamMemberStatus),
                // On error, emit 'error'
                catchError(err => {
                  console.error('Error adding team members:', err);
                  return of('error' as AddTeamMemberStatus);
                })
              )
            )
          );
        })
      );

      // Combine initial status, manual status updates, and API request statuses
      return of(initialStatus).pipe(
        // Merge with manual status updates and API request statuses
        switchMap(status => {
          return this.manualStatusSubject.pipe(
            // Start with the initial status
            startWith(status),
            // Merge with API request statuses
            switchMap(manualStatus => {
              // If the manual status is 'adding', we want to also include API request statuses
              if (manualStatus === 'adding') {
                return apiRequestStatus$.pipe(
                  // Start with 'adding'
                  startWith(manualStatus)
                );
              }
              // Otherwise, just emit the manual status
              return of(manualStatus);
            })
          );
        })
      );
    }),
    // Share the observable to prevent multiple HTTP requests
    share(),
    // Clean up when the service is destroyed
    takeUntilDestroyed(),
  );

  constructor() {
    // No explicit subscriptions needed
  }


  getTeamMembers(page = 0, pageSize = 10): Observable<ApiResponse<TeamMember[]> & { total: number }> {
    return getApiUrl('iam').pipe(
      switchMap(url => this.http.get<TeamMembersResponse>(
        `${url}/orgs/team-members?first=${(page * pageSize) + 1}&max=${pageSize}`
      ).pipe(
        exponentialBackoffRetry(),
        map(res => ({
          data: res.members,
          total: parseInt(res.count, 10)
        })),
        catchError(err => of({
          error: err,
          data: undefined,
          total: 0
        }))
      ))
    );
  }

  teamMemberInOrg(email: string): Observable<boolean> {
    return getApiUrl('iam').pipe(
      switchMap(url =>
        this.http.get<{ success: boolean; inOrg: boolean }>(`${url}/orgs/team-members/${email}`).pipe(
          exponentialBackoffRetry(),
          catchError(err => {
            console.error(err);
            return EMPTY;
          })
        )
      ),
      map(res => res.inOrg)
    );
  }

  addMembers(teamMembers: AddTeamMemberModel[]) {
    // First emit 'adding' status
    this.manualStatusSubject.next('adding');

    // Then push team members to the subject, which will trigger the API request
    // through the addTeamMemberStatus$ observable chain
    this.addTeamMembersSubject.next(teamMembers);
  }
}
